import { useEffect, useState, useRef, useCallback } from 'react';

/**
 * useTTSDetection - Détection robuste de l'état TTS par polling direct.
 * 
 * FIX V3: Élimine la race condition entre audio.play() et l'attachement
 * des event listeners. Utilise un polling à 50ms pour détecter directement
 * l'état de lecture (audio.paused / audio.ended) sans dépendre d'events DOM.
 * 
 * Web Audio API (AnalyserNode) est tenté pour le ttsAudioLevel réel.
 * En cas d'échec (AudioContext suspendu, CORS), un niveau simulé est généré.
 */
const useTTSDetection = (audioRef) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsAudioLevel, setTtsAudioLevel] = useState(0);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const sourceAudioRef = useRef(null);
  const dataArrayRef = useRef(null);

  // Simulated audio level for when Web Audio API fails
  const simPhaseRef = useRef(0);
  const analyzerWorking = useRef(false);

  // Manual notification (kept for API compatibility)
  const notifyAudioChange = useCallback(() => {
    // No-op in V3 — polling handles everything
  }, []);

  // Try to initialize Web Audio API analyzer for real audio level
  const tryInitAnalyzer = useCallback((audio) => {
    if (sourceAudioRef.current === audio) return; // Already initialized for this element

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      // Resume AudioContext if suspended (browser autoplay policy)
      if (audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }

      // CRITICAL: Only create MediaElementSource if AudioContext is running
      // Otherwise, routing audio through a suspended context would mute it
      if (audioContextRef.current.state !== 'running') {
        analyzerWorking.current = false;
        sourceAudioRef.current = audio; // Don't retry for this element
        return;
      }

      if (!analyserRef.current) {
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 512;
        analyserRef.current.smoothingTimeConstant = 0.6;
        analyserRef.current.connect(audioContextRef.current.destination);
      }

      // Disconnect previous source
      if (sourceRef.current) {
        try { sourceRef.current.disconnect(); } catch (e) { /* ignore */ }
        sourceRef.current = null;
      }

      const source = audioContextRef.current.createMediaElementSource(audio);
      source.connect(analyserRef.current);
      sourceRef.current = source;
      sourceAudioRef.current = audio;
      analyzerWorking.current = true;

      if (!dataArrayRef.current) {
        dataArrayRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
      }
    } catch (error) {
      // Common: "audio element already connected" or "AudioContext not allowed"
      analyzerWorking.current = false;
      sourceAudioRef.current = audio; // Don't retry for this element
    }
  }, []);

  // Read real audio level from Web Audio API
  const readRealAudioLevel = useCallback(() => {
    if (!analyserRef.current || !dataArrayRef.current) return -1;

    try {
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);
      const data = dataArrayRef.current;

      // Focus on speech-relevant bins (200-4000 Hz)
      const startBin = 2;
      const endBin = Math.min(42, data.length);
      let sum = 0;
      let peak = 0;
      for (let i = startBin; i < endBin; i++) {
        sum += data[i];
        if (data[i] > peak) peak = data[i];
      }
      const speechBins = endBin - startBin;
      const rmsLike = Math.sqrt(sum / speechBins) / 16;
      const peakLevel = peak / 255;
      const rawLevel = rmsLike * 0.6 + peakLevel * 0.4;
      return Math.min(rawLevel * 1.8, 1.0);
    } catch (e) {
      return -1;
    }
  }, []);

  // Main polling loop — 50ms interval for responsive detection
  useEffect(() => {
    let wasSpeaking = false;
    let zeroCount = 0;

    const poll = setInterval(() => {
      const audio = audioRef?.current;

      if (!audio) {
        if (wasSpeaking) {
          wasSpeaking = false;
          setIsSpeaking(false);
          setTtsAudioLevel(0);
        }
        return;
      }

      const isPlaying = !audio.paused && !audio.ended && audio.readyState >= 2;

      if (isPlaying && !wasSpeaking) {
        // Transition: not speaking → speaking
        wasSpeaking = true;
        setIsSpeaking(true);
        tryInitAnalyzer(audio);
        console.log('[useTTSDetection] V3: Speaking detected via polling');
      } else if (!isPlaying && wasSpeaking) {
        // Transition: speaking → not speaking
        wasSpeaking = false;
        setIsSpeaking(false);
        setTtsAudioLevel(0);
      }

      // Update audio level while speaking
      if (isPlaying) {
        let level = -1;

        // Try real analyzer first
        if (analyzerWorking.current) {
          level = readRealAudioLevel();
          // If analyzer consistently returns near-zero (suspended AudioContext), fall back to simulated
          if (level >= 0 && level < 0.01) {
            zeroCount++;
            if (zeroCount > 10) { // ~500ms of zeros → analyzer is dead
              analyzerWorking.current = false;
              level = -1;
            }
          } else {
            zeroCount = 0;
          }
        }

        if (level >= 0) {
          // Real audio level available
          setTtsAudioLevel(level);
        } else {
          // Simulated level — natural speech-like oscillation
          simPhaseRef.current += 0.05;
          const t = simPhaseRef.current;
          const base = 0.35;
          const variation = Math.sin(t * 6.3) * 0.15
            + Math.sin(t * 9.7) * 0.10
            + Math.sin(t * 2.1) * 0.08;
          const simLevel = Math.max(0, Math.min(1, base + variation + Math.random() * 0.05));
          setTtsAudioLevel(simLevel);
        }
      }
    }, 50);

    return () => clearInterval(poll);
  }, [audioRef, tryInitAnalyzer, readRealAudioLevel]);

  // Cleanup AudioContext on unmount
  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        try { sourceRef.current.disconnect(); } catch (e) { /* ignore */ }
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, []);

  return {
    isSpeaking,
    ttsAudioLevel,
    notifyAudioChange
  };
};

export default useTTSDetection;
